import { InlineNotification } from '@carbon/react';
import { StatsBar, type StatItem } from 'components/stats-bar';
import { format0 } from 'utils/number';
import { useReasonLabels } from './hooks';
import { OUTCOME_LABEL, OUTCOME_TAG_TYPE } from './utils';
import type {
  AutoMatchDecisionStats,
  AutoMatchInvariants,
  AutoMatchReversalRate,
} from './types';
import shared from '../admin-log.module.scss';

interface DecisionStatsProps {
  stats: AutoMatchDecisionStats;
  invariants: AutoMatchInvariants;
  reversalRate: AutoMatchReversalRate;
}

// The only committed reversal target.
const REVERSAL_TARGET = 0.5;

/**
 * A cohort's date range as prose.
 * @param {string} from - The first date
 * @param {string} to - The last date
 * @return {string} "from to to"
 */
const range = (from: string, to: string) => `${from} to ${to}`;

// Deliberately absent: precision. It needs the human-labelled QC sample
// this table does not hold, so any number here would be read as the 99.5% target
// being met.
/**
 * The decision log's stats strip, its scope note, and the invariant alarm.
 * @param {DecisionStatsProps} props - The filtered counts, invariants, and the global reversal rate
 * @return {JSX.Element} The stats header
 */
export function DecisionStats({
  stats,
  invariants,
  reversalRate,
}: DecisionStatsProps) {
  const reasonLabels = useReasonLabels();
  const items: StatItem[] = [
    {
      label: OUTCOME_LABEL.auto_confirmed,
      value: stats.autoConfirmed,
      tagType: OUTCOME_TAG_TYPE.auto_confirmed,
    },
    {
      label: OUTCOME_LABEL.manual_review,
      value: stats.manualReview,
      tagType: OUTCOME_TAG_TYPE.manual_review,
    },
  ];

  // Disabled-gate volume (county/feature off). Shown only when nonzero: it is
  // the gate never running, not a decision, so it sits apart from the rate and
  // a permanent 0 would just train people to stop reading it.
  if (stats.notEvaluated > 0) {
    items.push({
      label: 'Not evaluated (gate off)',
      value: stats.notEvaluated,
      tagType: 'cool-gray',
    });
  }

  if (stats.autoConfirmRate !== null) {
    items.push({
      label: 'Auto-confirm rate',
      value: stats.autoConfirmRate,
      suffix: '%',
      decimals: 1,
      tagType: 'blue',
    });
  }

  if (stats.topSuppression) {
    items.push({
      label: `Top suppressor — ${reasonLabels[stats.topSuppression.reason] ?? stats.topSuppression.reason}`,
      value: stats.topSuppression.count,
      tagType: 'purple',
    });
  }

  // A rate over an empty cohort is not 0%, it is "not enough history yet".
  if (reversalRate.rate !== null) {
    items.push({
      label: 'Reversal rate',
      value: reversalRate.rate,
      suffix: '%',
      // Sub-1% is the whole point of the target — two decimals so 0.33% does not
      // round to "0%" and disagree with the color keyed off the raw value.
      decimals: 2,
      tagType: reversalRate.rate >= REVERSAL_TARGET ? 'red' : 'teal',
    });
  }

  const breached = invariants.flaggedWinner + invariants.guardrailDisabled;

  return (
    <div className={shared.statsHeader}>
      <StatsBar stats={items} />

      <p className={shared.statsNote}>
        Counts follow the filters below. Reversal rate is global and cohorted by
        decision date over{' '}
        {range(reversalRate.cohortStart, reversalRate.cohortEnd)}, so only
        decisions old enough to have been reversed are counted
        {reversalRate.rate === null
          ? ' — no matured decisions yet.'
          : ` (${format0(reversalRate.undone)} of ${format0(
              reversalRate.cohortTotal
            )}).`}
      </p>

      {/* Rendered only when breached: a tile permanently reading 0 trains
          people to stop looking at it. */}
      {breached > 0 && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Hard invariant not enforced"
          subtitle={[
            invariants.flaggedWinner > 0 &&
              `${format0(
                invariants.flaggedWinner
              )} auto-confirmed to a warning/info flagged entity`,
            invariants.guardrailDisabled > 0 &&
              `${format0(
                invariants.guardrailDisabled
              )} auto-confirmed with the flag interlock or entity-type check switched off`,
          ]
            .filter(Boolean)
            .join('. ')}
        />
      )}
    </div>
  );
}
